import {ActorListEditor} from '@github-ui/actor-list-editor'
import type {ActorCategorySource, ActorSelectorSource} from '@github-ui/actor-list-editor/types'
import {getBypassSuggestions} from '@github-ui/bypass-actors/api'
import {availableBypassActorCategories, bypassActorCategory} from '@github-ui/bypass-actors/categories'
import {BypassModeEditor} from '@github-ui/bypass-actors/BypassModeEditor'
import {ActorBypassMode, type BypassActor, type SupportedBypassMode} from '@github-ui/bypass-actors/types'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import type {RulesetRoutePayload, RulesetTarget} from '@github-ui/repos-rules-types/rules-types'
import type {FC} from 'react'
import {useCallback, useMemo} from 'react'

import {capitalize} from '../../helpers/string'
import {useRelativeNavigation} from '../../hooks/use-relative-navigation'
import {useRuleStrings} from '../../hooks/use-rule-strings'
import {PanelHeader} from './conditions/PanelHeader'
import {LegacyBypassListPanel} from './LegacyBypassListPanel'

export type BypassListPanelProps = {
  readOnly?: boolean
  bypassActors: BypassActor[]
  setBypassActors: (bypassActors: BypassActor[]) => void
  addBypassActor: (
    actorId: BypassActor['actorId'],
    actorType: BypassActor['actorType'],
    preferredAvatarUrl: BypassActor['preferredAvatarUrl'],
    name: BypassActor['name'],
    bypassMode: BypassActor['bypassMode'],
    owner: BypassActor['owner'],
  ) => void
  rulesetTarget: RulesetTarget
  removeBypassActor: (bypassActor: BypassActor) => void
  updateBypassActor: (bypassActor: BypassActor) => void
}

const ActorListEditorBypassListPanel: FC<BypassListPanelProps> = ({
  readOnly,
  bypassActors,
  addBypassActor,
  removeBypassActor,
  rulesetTarget,
  updateBypassActor,
}: BypassListPanelProps) => {
  const {baseAvatarUrl, sourceType} = useRoutePayload<RulesetRoutePayload>()
  const {resolvePath} = useRelativeNavigation()
  const {rulesetOrPolicy} = useRuleStrings()

  const supportedBypassModes: SupportedBypassMode[] = useMemo(() => {
    const modes: SupportedBypassMode[] = [
      {
        mode: ActorBypassMode.ALWAYS,
        description: `The ${rulesetOrPolicy} will be evaluated and the selected actor(s) will be prompted to bypass`,
      },
      {
        mode: ActorBypassMode.EXEMPT,
        description: `The ${rulesetOrPolicy} will not be evaluated and no bypass prompt will be shown`,
      },
    ]
    if (rulesetTarget === 'branch') {
      modes.push({
        mode: ActorBypassMode.PRS_ONLY,
        description: `The ${rulesetOrPolicy} will be enforced on command line changes and the selected actor(s) will be prompted to bypass only in a pull request`,
      })
    }
    return modes
  }, [rulesetTarget, rulesetOrPolicy])

  const bypassSuggestionsUrl = resolvePath('bypass_suggestions')

  const enabledBypassActors = useMemo(() => {
    // The admin repository role is pinned to the top of the list.
    const isAdminRole = (actor: BypassActor) => actor.name === 'admin' && actor.actorType === 'RepositoryRole'
    const enabled = bypassActors.filter(({_enabled}) => _enabled)
    return [...enabled.filter(isAdminRole), ...enabled.filter(actor => !isAdminRole(actor))].map(actor => ({
      ...actor,
      actorCategory: bypassActorCategory(actor.actorType),
    }))
  }, [bypassActors])

  // The suggestions payload carries no category, so derive one client-side.
  const fetchSuggestions = useCallback(
    async (query: string) => {
      const suggestions = await getBypassSuggestions(bypassSuggestionsUrl, query)
      return suggestions.map(actor => ({...actor, actorCategory: bypassActorCategory(actor.actorType)}))
    },
    [bypassSuggestionsUrl],
  )

  const categories = useMemo<ActorCategorySource>(
    () => ({fetchCategories: async () => availableBypassActorCategories(sourceType)}),
    [sourceType],
  )

  const listName = rulesetTarget === 'repository' ? 'allow list' : 'bypass list'
  // Distinct from `listName`: names the box's contents (cf. TargetsPanel's two-tier headings).
  const listHeader = rulesetTarget === 'repository' ? 'Allowed actors' : 'Actors'
  // Repository rulesets are allow lists ("Add actors"); other targets are bypass lists ("Add bypass").
  const addLabel = rulesetTarget === 'repository' ? 'Add actors' : 'Add bypass'
  // Individual users are only suggested for repository rulesets.
  const reviewerGroup =
    sourceType === 'repository' ? 'roles, teams, agents, apps, and users' : 'roles, teams, agents, and apps'

  const selector = useMemo<ActorSelectorSource<BypassActor>>(
    () => ({
      fetchSuggestions,
      addButtonLabel: addLabel,
      panelTitle: () => addLabel,
      panelPlaceholder: 'Filter actors',
      panelSubtitle: `Choose which ${reviewerGroup} can bypass this ${rulesetOrPolicy}`,
    }),
    [addLabel, fetchSuggestions, reviewerGroup, rulesetOrPolicy],
  )

  const onAdd = useCallback(
    (actor: BypassActor) => {
      addBypassActor(
        actor.actorId,
        actor.actorType,
        actor.preferredAvatarUrl,
        actor.name,
        ActorBypassMode.ALWAYS,
        actor.owner,
      )
    },
    [addBypassActor],
  )

  const renderRowActions = useCallback(
    (actor: BypassActor) => (
      <BypassModeEditor
        actor={actor}
        readOnly={readOnly}
        updateBypassActor={updateBypassActor}
        supportedBypassModes={supportedBypassModes}
      />
    ),
    [readOnly, updateBypassActor, supportedBypassModes],
  )

  let subtext = `Exempt ${reviewerGroup} from this ${rulesetOrPolicy} by adding them to the ${listName}.`

  if (rulesetTarget === 'push') {
    if (sourceType === 'enterprise') {
      subtext = `Select the roles that can bypass and also approve bypass requests.`
    } else {
      subtext = `Select the roles and teams that can bypass and also approve bypass requests. Add the bots and agents that can bypass this ${rulesetOrPolicy}.`
    }
  }

  return (
    <ActorListEditor<BypassActor>
      title={capitalize(listHeader)}
      subtitle={<PanelHeader title={`${capitalize(listName)}`} subtitle={subtext} />}
      readOnly={readOnly}
      actors={enabledBypassActors}
      selector={selector}
      baseAvatarUrl={baseAvatarUrl}
      categories={categories}
      categoryFilterVariant="responsive"
      onAdd={onAdd}
      onRemove={removeBypassActor}
      renderRowActions={renderRowActions}
      emptyState={{
        heading: `${capitalize(listName)} is empty`,
        filteredHeading: cat => `No ${cat === 'Other' ? 'other actors' : cat.toLowerCase()} in ${listName}`,
      }}
    />
  )
}

export const BypassListPanel: FC<BypassListPanelProps> = props => {
  return isFeatureEnabled('rulesets_actor_list_editor') ? (
    <ActorListEditorBypassListPanel {...props} />
  ) : (
    <LegacyBypassListPanel {...props} />
  )
}
